import type { ScoutChatMessagePart, ScoutToolPart } from "./message/scoutChatMessage";

export type ScoutTodoStatus = "pending" | "in_progress" | "done" | "skipped";

export type ScoutTodoItem = {
  id: string;
  task: string;
  /** Customer-facing step name when provided by the agent. */
  label?: string;
  status: ScoutTodoStatus;
  result?: string;
  source?: string;
};

export type ScoutTodoSummary = {
  total: number;
  pending: number;
  in_progress: number;
  done: number;
  skipped: number;
};

export type ScoutTodoSnapshot = {
  items: ScoutTodoItem[];
  summary: ScoutTodoSummary;
  /** True once create_todos has been invoked in this tool group. */
  hasChecklist: boolean;
  /** True while create_todos is still running without a parsed item list. */
  isPlanning: boolean;
};

export type DeriveTodoSnapshotOptions = {
  /** When true, the owning assistant message is still streaming. Reserved for future streaming-specific behavior. */
  isStreaming?: boolean;
};

export const SCOUT_TODO_TOOL_NAMES = new Set([
  "create_todos",
  "update_todo",
  "update_todos",
  "get_todos",
]);

const TODO_STATUSES = new Set<ScoutTodoStatus>(["pending", "in_progress", "done", "skipped"]);

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

function readString(record: Record<string, unknown>, key: string): string | undefined {
  const value = record[key];
  return typeof value === "string" && value.length > 0 ? value : undefined;
}

function parseJsonObjectIfPossible(value: string): unknown {
  try {
    return JSON.parse(value);
  } catch {
    return null;
  }
}

/** Accept tool args as a decoded object or a JSON string (streamed / persisted). */
function normalizeToolInput(input: unknown): Record<string, unknown> | null {
  if (isRecord(input)) {
    return input;
  }
  if (typeof input === "string") {
    const parsed = parseJsonObjectIfPossible(input);
    return isRecord(parsed) ? parsed : null;
  }
  return null;
}

function parseTodoStatus(value: unknown): ScoutTodoStatus | null {
  if (typeof value === "string" && TODO_STATUSES.has(value as ScoutTodoStatus)) {
    return value as ScoutTodoStatus;
  }
  return null;
}

/**
 * Parse a field that may be either a JSON-encoded string (how the agent SDK
 * sends `create_todos.items` / `update_todos.updates`) or an already-decoded
 * array (how tests / direct calls pass it).
 */
function parseJsonArrayField(value: unknown, key: string): Record<string, unknown>[] {
  const record = normalizeToolInput(value);
  if (!record || !(key in record)) return [];
  let candidate = record[key];
  if (typeof candidate === "string") {
    try {
      candidate = JSON.parse(candidate);
    } catch {
      return [];
    }
  }
  if (!Array.isArray(candidate)) return [];
  return candidate.filter(isRecord);
}

function parseTodoItem(record: Record<string, unknown>): ScoutTodoItem | null {
  const id = readString(record, "id");
  if (!id) return null;

  const task = readString(record, "task") ?? id;
  const label = readString(record, "label");
  const status = parseTodoStatus(record.status) ?? "pending";
  const result = readString(record, "result");
  const source = readString(record, "source");

  return {
    id,
    task,
    status,
    ...(label ? { label } : {}),
    ...(result ? { result } : {}),
    ...(source ? { source } : {}),
  };
}

function parseTodoItemsFromInput(input: unknown, key: string): ScoutTodoItem[] {
  return parseJsonArrayField(input, key)
    .map(parseTodoItem)
    .filter((item): item is ScoutTodoItem => item !== null);
}

function parseTodoItemFromOutput(output: unknown): ScoutTodoItem | null {
  if (!isRecord(output)) return null;
  const itemRecord = isRecord(output.item) ? output.item : output;
  return parseTodoItem(itemRecord);
}

function parseTodoItemsFromOutput(output: unknown): ScoutTodoItem[] {
  if (!isRecord(output)) return [];
  const itemsValue = output.items;
  if (!Array.isArray(itemsValue)) return [];
  return itemsValue
    .filter(isRecord)
    .map(parseTodoItem)
    .filter((item): item is ScoutTodoItem => item !== null);
}

export function calculateTodoSummary(items: ScoutTodoItem[]): ScoutTodoSummary {
  return {
    total: items.length,
    pending: items.filter((item) => item.status === "pending").length,
    in_progress: items.filter((item) => item.status === "in_progress").length,
    done: items.filter((item) => item.status === "done").length,
    skipped: items.filter((item) => item.status === "skipped").length,
  };
}

function replaceAllItems(
  itemsById: Map<string, ScoutTodoItem>,
  order: string[],
  nextItems: ScoutTodoItem[]
): void {
  itemsById.clear();
  order.length = 0;
  for (const item of nextItems) {
    itemsById.set(item.id, item);
    order.push(item.id);
  }
}

function applyTodoUpdate(
  itemsById: Map<string, ScoutTodoItem>,
  order: string[],
  update: Partial<ScoutTodoItem> & { id: string }
): void {
  const existing = itemsById.get(update.id);
  const merged: ScoutTodoItem = {
    id: update.id,
    task: update.task ?? existing?.task ?? update.id,
    status: update.status ?? existing?.status ?? "pending",
    ...(update.result !== undefined
      ? { result: update.result }
      : existing?.result
        ? { result: existing.result }
        : {}),
    ...(update.label !== undefined
      ? { label: update.label }
      : existing?.label
        ? { label: existing.label }
        : {}),
    ...(update.source !== undefined
      ? { source: update.source }
      : existing?.source
        ? { source: existing.source }
        : {}),
  };

  if (!itemsById.has(update.id)) {
    order.push(update.id);
  }
  itemsById.set(update.id, merged);
}

function applyUpdateTodoFromInput(
  itemsById: Map<string, ScoutTodoItem>,
  order: string[],
  toolInput: unknown
): void {
  const inputRecord = normalizeToolInput(toolInput);
  if (!inputRecord) {
    return;
  }

  const id = readString(inputRecord, "id");
  const status = parseTodoStatus(inputRecord.status);
  if (!id || !status) {
    return;
  }

  applyTodoUpdate(itemsById, order, {
    id,
    status,
    ...(readString(inputRecord, "result") ? { result: readString(inputRecord, "result") } : {}),
    ...(readString(inputRecord, "source") ? { source: readString(inputRecord, "source") } : {}),
  });
}

function applyUpdateTodosFromInput(
  itemsById: Map<string, ScoutTodoItem>,
  order: string[],
  toolInput: unknown
): void {
  const inputUpdates = parseTodoItemsFromInput(toolInput, "updates");
  for (const update of inputUpdates) {
    applyTodoUpdate(itemsById, order, update);
  }
}

function isToolRunning(tool: ScoutToolPart): boolean {
  return tool.toolState === "input_streaming" || tool.toolState === "input_available";
}

/**
 * Reconstruct the current todo checklist from all tool parts on an assistant
 * message. Aggregates todo events across tool groups separated by text blocks.
 */
export function deriveTodoSnapshotFromMessageParts(
  parts: ScoutChatMessagePart[],
  _options?: DeriveTodoSnapshotOptions
): ScoutTodoSnapshot | null {
  const toolParts = parts.filter((part): part is ScoutToolPart => part.type === "tool");
  return deriveTodoSnapshotFromToolParts(toolParts);
}

/**
 * Reconstruct the current todo checklist from todo tool parts in stream order.
 * Returns null when the tool group contains no todo tools.
 */
export function deriveTodoSnapshotFromToolParts(
  toolParts: ScoutToolPart[]
): ScoutTodoSnapshot | null {
  const todoTools = toolParts.filter((tool) => SCOUT_TODO_TOOL_NAMES.has(tool.toolName));
  if (todoTools.length === 0) {
    return null;
  }

  const itemsById = new Map<string, ScoutTodoItem>();
  const order: string[] = [];
  let hasChecklist = false;
  let isPlanning = false;

  for (const tool of todoTools) {
    switch (tool.toolName) {
      case "create_todos": {
        hasChecklist = true;
        const items = parseTodoItemsFromInput(tool.toolInput, "items");
        if (items.length > 0) {
          replaceAllItems(itemsById, order, items);
          isPlanning = false;
        } else if (isToolRunning(tool)) {
          isPlanning = true;
        }
        break;
      }
      case "update_todo": {
        hasChecklist = true;
        const outputItem = parseTodoItemFromOutput(tool.toolOutput);
        if (outputItem) {
          applyTodoUpdate(itemsById, order, outputItem);
          break;
        }

        applyUpdateTodoFromInput(itemsById, order, tool.toolInput);
        break;
      }
      case "update_todos": {
        hasChecklist = true;
        const outputItems = parseTodoItemsFromOutput(tool.toolOutput);
        if (outputItems.length > 0) {
          for (const item of outputItems) {
            applyTodoUpdate(itemsById, order, item);
          }
          break;
        }

        applyUpdateTodosFromInput(itemsById, order, tool.toolInput);
        break;
      }
      // get_todos is ignored for UI state — verification only.
      case "get_todos":
        break;
      default:
        break;
    }
  }

  const items = order
    .map((id) => itemsById.get(id))
    .filter((item): item is ScoutTodoItem => item !== undefined);

  if (isPlanning && items.length > 0) {
    isPlanning = false;
  }

  return {
    items,
    summary: calculateTodoSummary(items),
    hasChecklist,
    isPlanning,
  };
}
